import type { Markup } from '@/types';
import { dimensionGeometry } from '@/core/geometry/dimension';
import { boxToPoints, normalizeMarkupGeometry } from './normalize';
import type { Box, Segment } from './types';

function boxFromSegments(segments: Segment[]): Box | null {
    if (segments.length === 0) return null;
    const points = segments.flatMap((segment) => [segment.start, segment.end]);
    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    return {
        left: Math.min(...xs),
        top: Math.min(...ys),
        right: Math.max(...xs),
        bottom: Math.max(...ys),
    };
}

export function getMarkupBounds(markup: Markup): Box | null {
    const geometry = normalizeMarkupGeometry(markup);
    if (!geometry) return null;

    switch (geometry.kind) {
        case 'point':
            return {
                left: geometry.point.x,
                top: geometry.point.y,
                right: geometry.point.x,
                bottom: geometry.point.y,
            };
        case 'segment': {
            const xs = [geometry.a.x, geometry.b.x];
            const ys = [geometry.a.y, geometry.b.y];
            return {
                left: Math.min(...xs),
                top: Math.min(...ys),
                right: Math.max(...xs),
                bottom: Math.max(...ys),
            };
        }
        case 'polyline':
        case 'polygon': {
            const xs = geometry.points.map((point) => point.x);
            const ys = geometry.points.map((point) => point.y);
            return {
                left: Math.min(...xs),
                top: Math.min(...ys),
                right: Math.max(...xs),
                bottom: Math.max(...ys),
            };
        }
        case 'box':
        case 'ellipse':
        case 'callout':
        case 'text':
        case 'group':
            return geometry.kind === 'callout'
                ? {
                    left: Math.min(geometry.tip.x, geometry.box.left),
                    top: Math.min(geometry.tip.y, geometry.box.top),
                    right: Math.max(geometry.tip.x, geometry.box.right),
                    bottom: Math.max(geometry.tip.y, geometry.box.bottom),
                }
                : geometry.kind === 'text'
                    ? geometry.estimatedBox
                    : geometry.box;
        case 'dimension': {
            const { a1, a2 } = dimensionGeometry(geometry.a, geometry.b, geometry.offset);
            return boxFromSegments([
                { start: geometry.a, end: geometry.b },
                { start: a1, end: a2 },
                { start: geometry.a, end: a1 },
                { start: geometry.b, end: a2 },
            ]);
        }
        default:
            return null;
    }
}

export function getBoxVertices(box: Box) {
    return boxToPoints(box);
}
